import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { apiLongRunningRequestTimeoutMs } from "../config";

export default function usePartDefaultCategoriesMutations() {
  const { post, patch } = useApiHelper();
  const { post: postLongRunning } = useApiHelper({ timeoutMs: apiLongRunningRequestTimeoutMs });
  const queryClient = useQueryClient();

  const invalidateDefaultCategoryQueries = (categoryIds?: string[]) => {
    queryClient.invalidateQueries(["parts"]);
    queryClient.invalidateQueries(["parts", "multiple"]);
    queryClient.invalidateQueries(["categories"]);
    queryClient.invalidateQueries(["pagedTable"]);

    if (categoryIds?.length) {
      categoryIds.forEach((categoryId) => {
        queryClient.invalidateQueries(["categoryParts", categoryId]);
      });
    }
  };


  const useDefaultPartCategory = useMutation<any, unknown, string>({
    mutationFn: (partId: string) => patch(`/part/${partId}/defaultcategory`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries(["parts"]);
      queryClient.invalidateQueries(["categories"]);
    },
  });

  const useBulkDefaultPartCategory = useMutation<any, unknown, { partIds: string[] }>({
    mutationFn: (body: { partIds: string[] }) => post(`/part/defaultcategory/bulk`, body),
    onSuccess: () => {
      queryClient.invalidateQueries(["parts"]);
      queryClient.invalidateQueries(["parts", "multiple"]);
      queryClient.invalidateQueries(["categories"]);
      queryClient.invalidateQueries(["pagedTable"]);
    },
  });

  const bulkApplyCategoryDefaultsToUnlisted = async (categoryId: string) => {
    const response = await post(`/part/defaultcategory/${categoryId}/bulk/unlisted`, {});
    invalidateDefaultCategoryQueries([categoryId]);

    return response;
  };

  const bulkApplyCategoryDefaultsToUnlistedForCategories = async (body: { categoryIds: string[] }) => {
    const response = await postLongRunning(`/part/defaultcategory/bulk/unlisted`, body);
    invalidateDefaultCategoryQueries(body.categoryIds);

    return response;
  };

  const bulkApplyPartCategoryDefaults = async (body: { partIds: string[] }) => {
    const response = await post(`/part/defaultcategory/bulk`, body);

    queryClient.invalidateQueries(["parts"]);
    queryClient.invalidateQueries(["parts", "multiple"]);
    queryClient.invalidateQueries(["categories"]);
    queryClient.invalidateQueries(["pagedTable"]);

    return response;
  };


  return {
    useDefaultPartCategory,
    useBulkDefaultPartCategory,
    bulkApplyCategoryDefaultsToUnlisted,
    bulkApplyCategoryDefaultsToUnlistedForCategories,
    bulkApplyPartCategoryDefaults,
  };
}
